import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Phone, Mail, Award, TrendingUp, Users, Target, Check, Star, Menu, X } from 'lucide-react';
import { services, benefits, testimonials, stats } from '../mock';

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const getIcon = (iconName) => {
    const icons = {
      Award,
      TrendingUp,
      Users,
      Target
    };
    const IconComponent = icons[iconName];
    return IconComponent ? <IconComponent className="w-8 h-8" /> : null;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm border-b border-slate-200 z-50">
        <nav className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">OE</span>
              </div>
              <span className="text-xl font-bold text-slate-900">Oaklawn Enterprise</span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('about')} className="text-slate-600 hover:text-amber-600 transition-colors font-medium">About</button>
              <button onClick={() => scrollToSection('services')} className="text-slate-600 hover:text-amber-600 transition-colors font-medium">Services</button>
              <button onClick={() => scrollToSection('testimonials')} className="text-slate-600 hover:text-amber-600 transition-colors font-medium">Testimonials</button>
              <button onClick={() => scrollToSection('contact')} className="text-slate-600 hover:text-amber-600 transition-colors font-medium">Contact</button>
              <Button onClick={() => scrollToSection('contact')} className="bg-amber-600 hover:bg-amber-700 text-white">Get Started</Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-slate-600 hover:text-amber-600"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-3">
              <button onClick={() => scrollToSection('about')} className="block w-full text-left px-4 py-2 text-slate-600 hover:text-amber-600 hover:bg-slate-50 rounded transition-colors font-medium">About</button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left px-4 py-2 text-slate-600 hover:text-amber-600 hover:bg-slate-50 rounded transition-colors font-medium">Services</button>
              <button onClick={() => scrollToSection('testimonials')} className="block w-full text-left px-4 py-2 text-slate-600 hover:text-amber-600 hover:bg-slate-50 rounded transition-colors font-medium">Testimonials</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left px-4 py-2 text-slate-600 hover:text-amber-600 hover:bg-slate-50 rounded transition-colors font-medium">Contact</button>
              <div className="px-4 pt-2">
                <Button onClick={() => scrollToSection('contact')} className="w-full bg-amber-600 hover:bg-amber-700 text-white">Get Started</Button>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative pt-24 lg:pt-32 pb-16 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-amber-50/30"></div>
        <div className="container mx-auto px-4 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100">20+ Years of Expertise</Badge>
              <h1 className="text-4xl lg:text-6xl font-bold text-slate-900 leading-tight">
                Master Drop Shipping & Amazon FBA
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed">
                Learn from two decades of proven experience. Transform your e-commerce dreams into a thriving business with expert consultation and personalized guidance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button onClick={() => scrollToSection('contact')} size="lg" className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6">
                  Schedule Consultation
                </Button>
                <Button onClick={() => scrollToSection('services')} size="lg" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50 text-lg px-8 py-6">
                  Learn More
                </Button>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8">
                {stats.map((stat, index) => (
                  <div key={index} className="space-y-1">
                    <div className="text-3xl font-bold text-amber-600">{stat.value}</div>
                    <div className="text-sm text-slate-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="aspect-square lg:aspect-auto lg:h-[600px] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1632406897798-e5472b4a989e"
                  alt="Professional Consultation"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-amber-500 rounded-2xl opacity-20"></div>
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-slate-800 rounded-2xl opacity-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative order-2 lg:order-1">
              <img
                src="https://images.unsplash.com/photo-1551135049-8a33b5883817"
                alt="Professional Team"
                className="rounded-2xl shadow-xl w-full h-[500px] object-cover"
              />
            </div>

            <div className="space-y-6 order-1 lg:order-2">
              <Badge className="bg-slate-100 text-slate-800 border-slate-200">About Us</Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-slate-900">
                Two Decades of E-commerce Excellence
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                With over 20 years of hands-on experience in the drop shipping and Amazon FBA space, Oaklawn Enterprise has helped countless entrepreneurs build successful online businesses from the ground up.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                We've navigated every challenge, adapted to every market shift, and perfected the craft of effective drop shipping. Now, we're here to share that expertise with you through personalized consultation and proven strategies.
              </p>
              
              <div className="space-y-4 pt-4">
                <div className="flex items-start space-x-3">
                  <Check className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">Proven track record of building 6 and 7-figure businesses</span>
                </div>
                <div className="flex items-start space-x-3">
                  <Check className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">Deep understanding of both drop shipping and FBA models</span>
                </div>
                <div className="flex items-start space-x-3">
                  <Check className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">Personalized strategies tailored to your unique goals</span>
                </div>
                <div className="flex items-start space-x-3">
                  <Check className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">Ongoing support to ensure long-term success</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 lg:py-24 bg-slate-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <Badge className="bg-amber-100 text-amber-800 border-amber-200">Our Services</Badge>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900">
              Expert Consultation Services
            </h2>
            <p className="text-lg text-slate-600">
              Whether you're just starting out or looking to scale your existing business, we provide the expertise you need to succeed.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {services.map((service) => (
              <Card key={service.id} className="border-slate-200 overflow-hidden hover:shadow-xl transition-all duration-300 group">
                <div className="aspect-video overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <CardHeader>
                  <CardTitle className="text-2xl text-slate-900">{service.title}</CardTitle>
                  <CardDescription className="text-base text-slate-600 mt-2">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <p className="font-semibold text-slate-900">What You'll Learn:</p>
                    <div className="grid grid-cols-2 gap-3">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <Check className="w-5 h-5 text-amber-600 flex-shrink-0" />
                          <span className="text-sm text-slate-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <Badge className="bg-slate-100 text-slate-800 border-slate-200">Why Choose Us</Badge>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900">
              Your Success Is Our Mission
            </h2>
            <p className="text-lg text-slate-600">
              We bring decades of real-world experience and a commitment to your growth.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit) => (
              <Card key={benefit.id} className="border-slate-200 text-center hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="w-16 h-16 mx-auto bg-amber-100 rounded-2xl flex items-center justify-center text-amber-600 mb-4">
                    {getIcon(benefit.icon)}
                  </div>
                  <CardTitle className="text-xl text-slate-900">{benefit.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-16 lg:py-24 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <Badge className="bg-amber-600 text-white border-amber-500">Testimonials</Badge>
            <h2 className="text-4xl lg:text-5xl font-bold text-white">
              Success Stories From Our Clients
            </h2>
            <p className="text-lg text-slate-300">
              Real results from real entrepreneurs who've transformed their businesses.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/15 transition-colors duration-300">
                <CardHeader>
                  <div className="flex space-x-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <CardDescription className="text-slate-200 text-base leading-relaxed">
                    "{testimonial.content}"
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    <p className="font-semibold text-white">{testimonial.name}</p>
                    <p className="text-sm text-slate-300">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12 space-y-4">
              <Badge className="bg-amber-100 text-amber-800 border-amber-200">Get In Touch</Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-slate-900">
                Ready to Transform Your Business?
              </h2>
              <p className="text-lg text-slate-600">
                Reach out today and take the first step toward e-commerce success.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <Card className="border-slate-200 hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center text-amber-600 mb-4">
                    <Phone className="w-7 h-7" />
                  </div>
                  <CardTitle className="text-2xl text-slate-900">Phone</CardTitle>
                  <CardDescription className="text-base text-slate-600 mt-2">
                    Call us for immediate consultation
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <a href="tel:614-832-0603" className="text-xl font-semibold text-amber-600 hover:text-amber-700 transition-colors">
                    614-832-0603
                  </a>
                </CardContent>
              </Card>

              <Card className="border-slate-200 hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center text-amber-600 mb-4">
                    <Mail className="w-7 h-7" />
                  </div>
                  <CardTitle className="text-2xl text-slate-900">Email</CardTitle>
                  <CardDescription className="text-base text-slate-600 mt-2">
                    Send us your questions anytime
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <a href="mailto:kh8382037@gmail.com" className="text-xl font-semibold text-amber-600 hover:text-amber-700 transition-colors break-all">
                    kh8382037@gmail.com
                  </a>
                </CardContent>
              </Card>
            </div>

            <div className="mt-12 p-8 bg-gradient-to-br from-slate-50 to-amber-50/30 rounded-2xl border border-slate-200 text-center space-y-4">
              <h3 className="text-2xl font-bold text-slate-900">
                Limited Consultation Slots Available
              </h3>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Due to high demand and our commitment to providing personalized attention, we only accept a limited number of clients each month. Contact us today to secure your spot.
              </p>
              <Button onClick={() => window.location.href = 'tel:614-832-0603'} size="lg" className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6">
                Call Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xl">OE</span>
                </div>
                <span className="text-xl font-bold">Oaklawn Enterprise</span>
              </div>
              <p className="text-slate-400">
                Empowering entrepreneurs with 20+ years of drop shipping and FBA expertise.
              </p>
            </div>

            <div className="space-y-4">
              <h4 className="text-lg font-semibold">Quick Links</h4>
              <div className="space-y-2">
                <button onClick={() => scrollToSection('about')} className="block text-slate-400 hover:text-amber-400 transition-colors">About</button>
                <button onClick={() => scrollToSection('services')} className="block text-slate-400 hover:text-amber-400 transition-colors">Services</button>
                <button onClick={() => scrollToSection('testimonials')} className="block text-slate-400 hover:text-amber-400 transition-colors">Testimonials</button>
                <button onClick={() => scrollToSection('contact')} className="block text-slate-400 hover:text-amber-400 transition-colors">Contact</button>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-lg font-semibold">Contact Info</h4>
              <div className="space-y-3">
                <a href="tel:614-832-0603" className="flex items-center space-x-3 text-slate-400 hover:text-amber-400 transition-colors">
                  <Phone className="w-5 h-5" />
                  <span>614-832-0603</span>
                </a>
                <a href="mailto:kh8382037@gmail.com" className="flex items-center space-x-3 text-slate-400 hover:text-amber-400 transition-colors">
                  <Mail className="w-5 h-5" />
                  <span>kh8382037@gmail.com</span>
                </a>
              </div>
            </div>
          </div>

          <Separator className="bg-slate-800 my-8" />

          <div className="text-center text-slate-400">
            <p>&copy; {new Date().getFullYear()} Oaklawn Enterprise. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
